import { Check, CornerDownRight } from 'lucide-react';

const agentTools = ['Claude', 'Cursor', 'Figma', 'MCP clients'];

export default function DocsDesignAuthorityVisual() {
  return (
    <section className="my-10 border-y border-gray-200 py-8 dark:border-brand-50/10" aria-labelledby="design-authority-operating-model">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <h2 id="design-authority-operating-model" className="m-0 max-w-[520px] text-[26px] font-semibold leading-[1.08] tracking-[-0.03em] text-gray-950 dark:text-white sm:text-[32px]">
          The work moves.<br />The authority stays.
        </h2>
        <p className="m-0 max-w-[260px] text-[12px] leading-relaxed text-gray-500 dark:text-brand-500 sm:text-right">
          One plan-scoped thread from agent request to accountable decision.
        </p>
      </div>

      <DesktopSpine />
      <MobileSpine />

      <div className="mt-7 flex items-start gap-2 border-t border-gray-200 pt-4 text-[12px] leading-relaxed text-gray-600 dark:border-brand-50/10 dark:text-brand-400">
        <CornerDownRight className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" strokeWidth={1.75} aria-hidden="true" />
        <span>Every job keeps the request, artifact, evidence, finding, and decision in one traceable history.</span>
      </div>
    </section>
  );
}

function DesktopSpine() {
  return (
    <div className="mt-10 hidden sm:block">
      <div className="relative grid min-h-[178px] grid-cols-[1fr_1.05fr_1fr]">
        <div className="absolute left-[8%] right-[8%] top-[68px] h-px bg-gray-300 dark:bg-brand-700" aria-hidden="true" />

        <div className="relative z-10 pr-8">
          <p className="m-0 text-[11px] font-semibold text-gray-950 dark:text-brand-100">Agent workspace</p>
          <div className="mt-3 flex max-w-[180px] flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-brand-500">
            {agentTools.map((tool) => <span key={tool}>{tool}</span>)}
          </div>
          <SpineNode className="mt-[25px]" />
          <p className="mb-0 mt-4 max-w-[180px] text-[12px] leading-relaxed text-gray-500 dark:text-brand-500">
            People and agents request work and return evidence.
          </p>
        </div>

        <div className="relative z-10 flex flex-col items-center text-center">
          <div className="flex h-[138px] w-[138px] flex-col items-center justify-center rounded-full bg-gray-950 px-5 text-white shadow-[0_18px_40px_-24px_rgba(15,23,42,0.8)] dark:bg-white dark:text-gray-950">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-400 dark:text-emerald-600">Pabs</span>
            <span className="mt-2 text-[16px] font-semibold leading-tight">Design<br />Authority</span>
          </div>
          <p className="mb-0 mt-4 max-w-[190px] text-[12px] leading-relaxed text-gray-700 dark:text-brand-300">
            Applies the contract and runs the governed job.
          </p>
        </div>

        <div className="relative z-10 flex flex-col items-end pl-8 text-right">
          <p className="m-0 text-[11px] font-semibold text-gray-950 dark:text-brand-100">Accountable lead</p>
          <p className="mb-0 mt-3 max-w-[190px] text-[11px] leading-relaxed text-gray-500 dark:text-brand-500">Follows the work in Pabs</p>
          <SpineNode className="mt-[25px]" terminal />
          <p className="mb-0 mt-4 max-w-[190px] text-[12px] leading-relaxed text-gray-500 dark:text-brand-500">
            Promotes, constrains, excepts, or escalates with human judgment.
          </p>
        </div>
      </div>
    </div>
  );
}

function MobileSpine() {
  return (
    <div className="relative mt-8 space-y-8 pl-8 sm:hidden">
      <div className="absolute bottom-4 left-[7px] top-3 w-px bg-gray-300 dark:bg-brand-700" aria-hidden="true" />
      <MobileStage title="Agent workspace" text="Claude, Cursor, Figma, and other MCP clients request work and return evidence." />
      <MobileStage title="Pabs Design Authority" text="Applies the active contract and runs the matching governed job." emphasis />
      <MobileStage title="Accountable lead" text="Follows the trail in Pabs and makes decisions that need human judgment." terminal />
    </div>
  );
}

function MobileStage({ title, text, emphasis = false, terminal = false }) {
  return (
    <div className="relative">
      <span className={`absolute -left-8 top-0 flex h-[15px] w-[15px] items-center justify-center rounded-full ${
        emphasis
          ? 'bg-gray-950 ring-4 ring-gray-100 dark:bg-white dark:ring-brand-800'
          : terminal
            ? 'bg-emerald-500'
            : 'border-2 border-gray-400 bg-white dark:border-brand-500 dark:bg-brand-900'
      }`}>
        {terminal && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} aria-hidden="true" />}
      </span>
      <h3 className="m-0 text-[13px] font-semibold text-gray-950 dark:text-brand-100">{title}</h3>
      <p className="mb-0 mt-2 text-[12px] leading-relaxed text-gray-500 dark:text-brand-500">{text}</p>
    </div>
  );
}

function SpineNode({ className = '', terminal = false }) {
  return (
    <span className={`flex h-[15px] w-[15px] items-center justify-center rounded-full ${
      terminal
        ? 'bg-emerald-500 ring-4 ring-emerald-50 dark:ring-emerald-950/40'
        : 'border-2 border-gray-400 bg-white dark:border-brand-500 dark:bg-brand-900'
    } ${className}`} aria-hidden="true">
      {terminal && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
    </span>
  );
}
